import { Timestamp } from 'firebase/firestore';

// Permission Categories for grouping related permissions
export interface PermissionCategory {
  id: string;
  name: string;
  displayName: string;
  description: string;
  icon: string;
  order: number;
  isSystem: boolean; // System categories cannot be deleted
}

// Individual Permission Definition
export interface Permission {
  id: string; // Format: resource.action (e.g., 'developments.create')
  categoryId: string;
  name: string;
  displayName: string;
  description: string;
  resource: string; // e.g., 'developments', 'users', 'reports'
  action: string; // e.g., 'create', 'read', 'update', 'delete', 'export'
  isSystemPermission: boolean; // Cannot be removed from super users
  dependencies?: string[]; // Other permissions required for this to work
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

// Role Definition
export interface RoleDefinition {
  id: string;
  name: string; // System name (e.g., 'super_user')
  displayName: string; // Display name (e.g., 'Super User')
  description: string;
  isSystemRole: boolean; // Cannot be deleted
  isCustom: boolean; // Created by users vs system default
  parentRole?: string; // For inheritance
  color?: string; // For UI display
  icon?: string; // For UI display
  order: number; // Display order
  createdBy?: string;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

// Dashboard cards that can be accessed
export enum DashboardCard {
  HQ = 'hq',
  PARTNER = 'partner',
  CRM = 'crm',
  ADVERTISING = 'advertising',
  LEAD_STATS = 'leadStats',
  VIEWING_MANAGEMENT = 'viewingManagement',
  CONSULTATIONS = 'consultations',
  TRAINING = 'training'
}

// Menu group definition for hierarchical permissions
export interface MenuGroup {
  id: string;
  name: string;
  description: string;
  cardId: string;
  icon?: string;
  subItems: SubMenuItem[];
}

// Sub-menu item definition
export interface SubMenuItem {
  id: string;
  name: string;
  description: string;
  groupId: string;
  action?: string;
  path?: string; // Route path for navigation (alternative to action)
  requiresPasswordProtection?: boolean;
}

// Hierarchical permission structure for detailed access control
export interface HierarchicalPermission {
  dashboardCards: {
    [cardId: string]: {
      enabled: boolean;
      menuGroups: {
        [groupId: string]: {
          enabled: boolean;
          subItems: {
            [itemId: string]: boolean;
          };
        };
      };
    };
  };
}

// Menu Access Control
export interface MenuAccess {
  menuId: string;
  visible: boolean;
  subMenus?: {
    id: string;
    visible: boolean;
  }[];
}

// Data Access Levels
export enum DataAccessLevel {
  OWN = 'own', // Only own created data
  TEAM = 'team', // Team members' data
  PARTNER = 'partner', // All partner data
  ALL = 'all' // System-wide access
}

// Role Permissions Mapping
export interface RolePermissions {
  id?: string;
  roleId: string;
  permissions: string[]; // Array of permission IDs (legacy flat structure)
  hierarchicalPermissions?: HierarchicalPermission; // New hierarchical structure
  storagePermissions?: StoragePermissions; // Storage read/write permissions
  menuAccess: MenuAccess[];
  customSettings: {
    maxUsers?: number;
    maxDevelopments?: number;
    maxReservations?: number;
    commissionCap?: number;
    dataAccessLevel: DataAccessLevel;
    canExport?: boolean;
    canBulkEdit?: boolean;
    canDeletePermanently?: boolean;
  };
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
  updatedBy?: string;
}

// Partner-specific role configuration
export interface PartnerRole {
  id?: string;
  partnerId: string; // Partner user ID
  partnerName: string;
  roleId: string; // Base role ID
  customName?: string; // Partner's custom name for the role
  customDisplayName?: string;
  permissions: string[]; // Additional permissions granted
  restrictions: string[]; // Permissions to remove from base role
  limits: {
    maxAgents: number;
    maxAdmins: number;
    maxCustomRoles: number;
  };
  dataIsolation: boolean; // Whether agents can only see partner data
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

// Permission Check Result
export interface PermissionCheckResult {
  hasPermission: boolean;
  reason?: string; // Why permission was denied
  requiredPermissions?: string[]; // What's missing
}

// Permission Change Log for Audit
export interface PermissionChangeLog {
  id?: string;
  roleId: string;
  changedBy: string;
  changeType: 'grant' | 'revoke' | 'modify';
  permissions: string[];
  previousPermissions?: string[];
  reason?: string;
  timestamp: Timestamp;
}

// Predefined Permission Sets (Templates)
export interface PermissionTemplate {
  id: string;
  name: string;
  description: string;
  category: 'sales' | 'admin' | 'finance' | 'support' | 'custom';
  permissions: string[];
  recommendedFor: string[]; // Role IDs this template is good for
}

// Permission Resources - Define all resources in the system
export const PERMISSION_RESOURCES = {
  DEVELOPMENTS: 'developments',
  USERS: 'users',
  RESERVATIONS: 'reservations',
  DEALS: 'deals',
  REPORTS: 'reports',
  CONTACTS: 'contacts',
  SETTINGS: 'settings',
  ROLES: 'roles',
  PARTNERS: 'partners',
  COMMISSIONS: 'commissions',
  DOCUMENTS: 'documents',
  ACTIVITIES: 'activities',
  INVOICES: 'invoices',
  VIEWINGS: 'viewings'
} as const;

// Permission Actions - Define all possible actions
export const PERMISSION_ACTIONS = {
  CREATE: 'create',
  READ: 'read',
  UPDATE: 'update',
  DELETE: 'delete',
  EXPORT: 'export',
  IMPORT: 'import',
  APPROVE: 'approve',
  CANCEL: 'cancel',
  EXTEND: 'extend',
  ASSIGN: 'assign',
  MANAGE: 'manage',
  VIEW_ALL: 'view_all',
  VIEW_OWN: 'view_own',
  VIEW_FINANCIAL: 'view_financial'
} as const;

// Helper type for permission IDs
export type PermissionId = `${typeof PERMISSION_RESOURCES[keyof typeof PERMISSION_RESOURCES]}.${typeof PERMISSION_ACTIONS[keyof typeof PERMISSION_ACTIONS]}`;

// User-specific permission overrides
export interface UserCustomPermission {
  id?: string;
  userId: string; // User document ID
  userEmail: string; // For display purposes
  userName: string; // For display purposes
  grantedPermissions: string[]; // Additional permissions beyond role
  revokedPermissions: string[]; // Permissions to remove from role
  hierarchicalOverrides?: HierarchicalPermission; // Custom menu/card access
  grantedBy: string; // User ID who granted these permissions
  grantedByName: string; // Name of user who granted
  reason?: string; // Why these permissions were granted/revoked
  expiresAt?: Timestamp; // Optional expiry date
  createdAt: Timestamp;
  updatedAt: Timestamp;
  isActive: boolean;
  notes?: string; // Additional notes about this override
}

// Combined permission result for a user
export interface UserEffectivePermissions {
  rolePermissions: string[]; // Base permissions from role
  customGranted: string[]; // Additional permissions
  customRevoked: string[]; // Removed permissions
  effectivePermissions: string[]; // Final computed permissions
  hierarchicalPermissions: HierarchicalPermission; // Combined hierarchical permissions
  storagePermissions?: StoragePermissions; // Storage path permissions
  source: 'role' | 'custom' | 'mixed'; // Where permissions come from
}

// Storage Permission Types
export interface StoragePathPermission {
  read: boolean;
  write: boolean;
}

export interface StoragePermissions {
  developments: StoragePathPermission;
  deals: StoragePathPermission;
  reservations: StoragePathPermission;
  consultantLoans: StoragePathPermission;
  documents: StoragePathPermission;
  invoices: StoragePathPermission;
  marketingAssets: StoragePathPermission;
  emailTemplates: StoragePathPermission;
  userPhotos: StoragePathPermission;
  viewings: StoragePathPermission;
  templates: StoragePathPermission;
}

// Storage path categories for mapping
export const STORAGE_PATH_CATEGORIES = [
  'developments',
  'deals',
  'reservations',
  'consultantLoans',
  'documents',
  'invoices',
  'marketingAssets',
  'emailTemplates',
  'userPhotos',
  'viewings',
  'templates',
] as const;

export type StoragePathCategory = typeof STORAGE_PATH_CATEGORIES[number];

// Default permission categories
export const DEFAULT_PERMISSION_CATEGORIES: PermissionCategory[] = [
  {
    id: 'development_management',
    name: 'development_management',
    displayName: 'Development Management',
    description: 'Permissions related to property developments',
    icon: 'Building2',
    order: 1,
    isSystem: true
  },
  {
    id: 'user_management',
    name: 'user_management',
    displayName: 'User Management',
    description: 'Permissions for managing users and roles',
    icon: 'Users',
    order: 2,
    isSystem: true
  },
  {
    id: 'sales_reservations',
    name: 'sales_reservations',
    displayName: 'Sales & Reservations',
    description: 'Permissions for sales activities and reservations',
    icon: 'ShoppingCart',
    order: 3,
    isSystem: true
  },
  {
    id: 'financial_reports',
    name: 'financial_reports',
    displayName: 'Financial & Reports',
    description: 'Permissions for financial data and reporting',
    icon: 'DollarSign',
    order: 4,
    isSystem: true
  },
  {
    id: 'crm_contacts',
    name: 'crm_contacts',
    displayName: 'CRM & Contacts',
    description: 'Permissions for contact management',
    icon: 'UserCheck',
    order: 5,
    isSystem: true
  },
  {
    id: 'system_admin',
    name: 'system_admin',
    displayName: 'System Administration',
    description: 'System-level administrative permissions',
    icon: 'Settings',
    order: 6,
    isSystem: true
  },
  {
    id: 'partner_management',
    name: 'partner_management',
    displayName: 'Partner Management',
    description: 'Permissions for partner-specific features',
    icon: 'Briefcase',
    order: 7,
    isSystem: true
  }
];